import axios from 'axios';
import { Dispatch } from 'redux';
import { BookingState } from "@/data/types";
import { RootState } from '../store'; // Assuming you have a RootState type for your Redux store
import api from './api';

// Action Types
const ADD_TO_CART_REQUEST = 'ADD_TO_CART_REQUEST';
const ADD_TO_CART_SUCCESS = 'ADD_TO_CART_SUCCESS';
const ADD_TO_CART_FAILURE = 'ADD_TO_CART_FAILURE';
const GET_CART_REQUEST = 'GET_CART_REQUEST';
const GET_CART_SUCCESS = 'GET_CART_SUCCESS';
const GET_CART_FAILURE = 'GET_CART_FAILURE';
const DELETE_CART_REQUEST = 'DELETE_CART_REQUEST';
const DELETE_CART_SUCCESS = 'DELETE_CART_SUCCESS';
const DELETE_CART_FAILURE = 'DELETE_CART_FAILURE';
const EMPTY_CART_REQUEST = 'EMPTY_CART_REQUEST';
const EMPTY_CART_SUCCESS = 'EMPTY_CART_SUCCESS';
const EMPTY_CART_FAILURE = 'EMPTY_CART_FAILURE';
const UPDATE_CART_TRAVELER_INFO_REQUEST = "UPDATE_CART_TRAVELER_INFO_REQUEST";
const UPDATE_CART_TRAVELER_INFO_SUCCESS = "UPDATE_CART_TRAVELER_INFO_SUCCESS";
const UPDATE_CART_TRAVELER_INFO_FAILURE = "UPDATE_CART_TRAVELER_INFO_FAILURE";

// Action Interfaces
interface AddToCartRequestAction {
  type: typeof ADD_TO_CART_REQUEST;
}

interface AddToCartSuccessAction {
  type: typeof ADD_TO_CART_SUCCESS;
  payload: BookingState;
}

interface AddToCartFailureAction {
  type: typeof ADD_TO_CART_FAILURE;
  error: string;
}

interface GetCartRequestAction {
  type: typeof GET_CART_REQUEST;
}

interface GetCartSuccessAction {
  type: typeof GET_CART_SUCCESS;
  payload: BookingState[];
}

interface GetCartFailureAction {
  type: typeof GET_CART_FAILURE;
  error: string;
}

interface DeleteCartRequestAction {
  type: typeof DELETE_CART_REQUEST;
}

interface DeleteCartSuccessAction {
  type: typeof DELETE_CART_SUCCESS;
  payload: string; // Assuming payload is the UUID of the deleted cart item
}

interface DeleteCartFailureAction {
  type: typeof DELETE_CART_FAILURE;
  error: string;
}

interface EmptyCartRequestAction {
  type: typeof EMPTY_CART_REQUEST;
}

interface EmptyCartSuccessAction {
  type: typeof EMPTY_CART_SUCCESS;
}

interface EmptyCartFailureAction {
  type: typeof EMPTY_CART_FAILURE;
  error: string;
}

interface UpdateCartTravelerInfoRequestAction {
  type: typeof UPDATE_CART_TRAVELER_INFO_REQUEST;
}

interface UpdateCartTravelerInfoSuccessAction {
  type: typeof UPDATE_CART_TRAVELER_INFO_SUCCESS;
  payload: {
    index: number;
    payload: Partial<BookingState>;
  };
}

interface UpdateCartTravelerInfoFailureAction {
  type: typeof UPDATE_CART_TRAVELER_INFO_FAILURE;
  error: string;
}


type CartActions =
  | AddToCartRequestAction
  | AddToCartSuccessAction
  | AddToCartFailureAction
  | GetCartRequestAction
  | GetCartSuccessAction
  | GetCartFailureAction
  | DeleteCartRequestAction
  | DeleteCartSuccessAction
  | DeleteCartFailureAction
  | EmptyCartRequestAction
  | EmptyCartSuccessAction
  | EmptyCartFailureAction
  | UpdateCartTravelerInfoRequestAction
  | UpdateCartTravelerInfoSuccessAction
  | UpdateCartTravelerInfoFailureAction;

// State Type
interface CartState {
  items: BookingState[];
  loading: boolean;
  error: string | null;
}

// Initial State
/**
 * Met une ligne de panier au format attendu par l'interface, quelle que soit sa
 * provenance.
 *
 * GET /api/cart renvoie `datetime: { date, time }`, `travellers: { adults… }` et
 * les tarifs charges (`schedulePricing` / `legacyPricing`). POST /api/cart/add
 * renvoie la LIGNE BRUTE : `datetime` en texte, `travellersAdult` et consorts,
 * aucun tour ni tarif. Elle etait ajoutee telle quelle au panier : le recapitulatif
 * du checkout affichait alors « À confirmer », « — », 0,00 € et « indisponible »,
 * et le bandeau rouge empechait de payer (GT-260916-VW2M).
 */
const normalizeCartItem = (item: any) => {
  if (!item || typeof item !== 'object') return item;

  let datetime = item.datetime;
  if (typeof datetime === 'string') {
    const [date = null, timeWithSeconds = ''] = datetime.split(' ');
    datetime = { date, time: timeWithSeconds.split(':').slice(0, 2).join(':') || null };
  } else if (datetime instanceof Date) {
    const [date, rest] = datetime.toISOString().split('T');
    datetime = { date, time: rest.split(':').slice(0, 2).join(':') };
  } else if (!datetime || typeof datetime !== 'object') {
    datetime = { date: null, time: null };
  }

  const travellers = item.travellers && typeof item.travellers === 'object'
    ? item.travellers
    : {
        adults: item.travellersAdult || 0,
        children: item.travellersChildren || 0,
        infants: item.travellersInfants || 0,
      };

  return {
    ...item,
    datetime,
    travellers,
    // Un tarif absent reste absent : le checkout doit pouvoir dire « prix inconnu »
    // plutot que d'afficher une reservation gratuite.
    pricing: item.schedulePricing || item.legacyPricing || item.pricing || null,
  };
};

const initialState: CartState = {
  items: [],
  loading: false,
  error: null,
};

// Reducer
const cartReducer = (state: CartState = initialState, action: CartActions): CartState => {
  switch (action.type) {
    case ADD_TO_CART_REQUEST:
    case GET_CART_REQUEST:
    case DELETE_CART_REQUEST:
    case EMPTY_CART_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case ADD_TO_CART_SUCCESS:
      return {
        ...state,
        loading: false,
        items: [...state.items, normalizeCartItem(action.payload)],
      };
    case GET_CART_SUCCESS:
      // Normalize cart items to use schedulePricing as the authoritative source
      const normalizedItems = action.payload.map(normalizeCartItem);
      return {
        ...state,
        loading: false,
        items: normalizedItems,
      };
    case DELETE_CART_SUCCESS:
      return {
        ...state,
        loading: false,
        items: state.items.filter(item => item.uuid !== action.payload), // Filter out the deleted item
      };
    case EMPTY_CART_SUCCESS:
      return {
        ...state,
        loading: false,
        items: [], // Empty the cart items
      };
    case ADD_TO_CART_FAILURE:
    case GET_CART_FAILURE:
    case DELETE_CART_FAILURE:
    case EMPTY_CART_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error,
      };
    case UPDATE_CART_TRAVELER_INFO_REQUEST:{
      return {
        ...state,
        loading: true,
        error: null
      };
    }
    case UPDATE_CART_TRAVELER_INFO_SUCCESS: {
      const { index, payload: travelerInfo } = action.payload;

      const updatedItems = [...state.items];
      updatedItems[index] = {
        ...updatedItems[index],
        ...travelerInfo, // flatten the traveler info into the item
      };

      return {
        ...state,
        loading: false,
        items: updatedItems
      };
    }
    case UPDATE_CART_TRAVELER_INFO_FAILURE:{
      return {
        ...state,
        loading: false,
        error: action.error
      };
    }
    default:
      return state;
  }
};

export const updateCartTravelerInfo = (index: number, travelerData: any) => async (dispatch: Dispatch, getState: () => RootState) => {
  dispatch({ type: UPDATE_CART_TRAVELER_INFO_REQUEST });

  const state = getState();
  const cartItem = state.cart.items[index];
  const cartUuid = cartItem.uuid;

  const userString = localStorage.getItem('user');
  let accessToken = '';
  if (userString) {
    const user = JSON.parse(userString);
    accessToken = user.access_token;
  }

  try {
    await api.put(`/api/cart/travelers-info/${cartUuid}`, travelerData, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    // Save locally too
    dispatch({
      type: UPDATE_CART_TRAVELER_INFO_SUCCESS,
      payload: { index, payload: travelerData }
    });
  } catch (error) {
    dispatch({
      type: UPDATE_CART_TRAVELER_INFO_FAILURE,
      error: "Failed to persist traveler information"
    });
  }
};


// Action Creators
export const addToCart = (booking: BookingState) => async (dispatch: Dispatch, getState: () => RootState) => {
  dispatch({ type: ADD_TO_CART_REQUEST });
  const userString = localStorage.getItem('user');
  let accessToken = '';
  if (userString) {
    const user = JSON.parse(userString);
    accessToken = user.access_token;
  }

  try {
    const response = await api.post(`/api/cart/add`, {
      ...booking,
      price: booking.price  // Ensure price is sent in the request body
    }, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    dispatch({ type: ADD_TO_CART_SUCCESS, payload: response.data });

    // La ligne creee n'embarque ni le tour ni son tarif : on relit le panier, qui
    // fait foi. Sans cela, le checkout ouvert juste apres (tunnel invite puis
    // connexion) restait sur la ligne brute, sans prix — GT-260916-VW2M.
    const state = getState() as any;
    const language =
      state?.language?.selectedLanguage || (booking as any)?.guideLanguage || 'en_US';
    await (getCart(language) as any)(dispatch);
  } catch (error) {
    let errorMessage = 'An unknown error occurred';
    if (axios.isAxiosError(error)) {
      errorMessage = error.response?.data?.message || error.message;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    dispatch({ type: ADD_TO_CART_FAILURE, error: errorMessage });
  }
};

export const getCart = (selectedLanguage: string) => async (dispatch: Dispatch) => {
  dispatch({ type: GET_CART_REQUEST });
  const userString = localStorage.getItem('user');
  let accessToken = '';
  if (userString) {
    const user = JSON.parse(userString);
    accessToken = user.access_token;
  }

  try {
    const response = await api.get(`/api/cart`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      params: {
        language: selectedLanguage,
      }
    });
    dispatch({ type: GET_CART_SUCCESS, payload: response.data });
  } catch (error) {
    let errorMessage = 'An unknown error occurred';
    if (axios.isAxiosError(error)) {
      errorMessage = error.response?.data?.message || error.message;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    dispatch({ type: GET_CART_FAILURE, error: errorMessage });
  }
};

export const deleteCart = (uuid: string) => async (dispatch: Dispatch, getState: () => RootState) => {
  dispatch({ type: DELETE_CART_REQUEST });
  const userString = localStorage.getItem('user');
  let accessToken = '';
  if (userString) {
    const user = JSON.parse(userString);
    accessToken = user.access_token;
  }

  try {
    await api.put(`/api/cart`, {
      uuid: uuid,
      status: 'deleted' // Update the status to 'deleted' instead of removing the cart item
    }, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      }
    });
    dispatch({ type: DELETE_CART_SUCCESS, payload: uuid });
  } catch (error) {
    let errorMessage = 'An unknown error occurred';
    if (axios.isAxiosError(error)) {
      errorMessage = error.response?.data?.message || error.message;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    dispatch({ type: DELETE_CART_FAILURE, error: errorMessage });
  }
};

export const emptyCart = () => async (dispatch: Dispatch, getState: () => RootState) => {
  dispatch({ type: EMPTY_CART_REQUEST });
  const userString = localStorage.getItem('user');
  let accessToken = '';
  if (userString) {
    const user = JSON.parse(userString);
    accessToken = user.access_token;
  }

  try {
    await api.post(`/api/empty-cart`, {}, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      }
    });
    dispatch({ type: EMPTY_CART_SUCCESS });
  } catch (error) {
    let errorMessage = 'An unknown error occurred';
    if (axios.isAxiosError(error)) {
      errorMessage = error.response?.data?.message || error.message;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    dispatch({ type: EMPTY_CART_FAILURE, error: errorMessage });
  }
};

export default cartReducer;
